import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { getFragmentData } from "../../__generated__/fragment-masking";
import { ClientContactsDocument, ContactFieldsFragmentDoc } from "../../__generated__/graphql";
import { useClientId } from "../../shared";

export type ContactItem = {
  id: string;
  name: string;
  isPrimary: boolean;
  email: string;
  phone: string;
};

export function useClientContacts() {
  const clientId = useClientId();
  const result = useQuery(ClientContactsDocument, {
    skip: !clientId,
    fetchPolicy: "cache-first",
    variables: { filters: { clientId } },
  });

  const contacts = useMemo(() => {
    const list = result.data?.clientContacts.contacts;
    if (!list) return [];
    const unmasked = getFragmentData(ContactFieldsFragmentDoc, list);
    // Primary-first stable sort — every consumer wants this ordering.
    return [...unmasked].sort((a, b) => Number(b.primary) - Number(a.primary));
  }, [result.data?.clientContacts.contacts]);

  const contactLists = useMemo<ContactItem[]>(() => {
    return contacts.map((contact) => ({
      id: contact.id,
      name: `${contact.firstName} ${contact.lastName}`.trim(),
      isPrimary: contact.primary,
      email: contact.email[0] ?? "",
      phone: contact.phoneNumber[0] ?? "",
    }));
  }, [contacts]);

  const count = result.data?.clientContacts.count ?? 0;

  return { ...result, contacts, contactLists, count };
}
